<template>
  <div class="hv">
    <div class="hv__row">
      <button class="hv__toggle" :disabled="!items || !items.length" @click="expanded = !expanded">
        <i v-if="items && items.length" :class="expanded ? 'el-icon-arrow-down' : 'el-icon-arrow-right'"></i>
        <span class="hv__label">{{ meta.title }}</span>
      </button>

      <span class="hv__count" v-if="items && items.length">
        {{ items.length }} {{ items.length === 1 ? "stavka" : "stavki" }}
      </span>

      <span class="hv__value" :class="{ 'hv__value--zero': !meta.value }">
        {{ formatValue(meta.value.toFixed(2)) }}<span class="hv__unit">{{ meta.unit }}</span>
      </span>

      <el-button
        class="hv__add"
        icon="el-icon-plus"
        size="mini"
        circle
        :title="'Dodaj u: ' + meta.title"
        @click="showAddDialog"
        :disabled="closed"
      ></el-button>
    </div>

    <ul v-show="expanded && items && items.length" class="hv__list">
      <li v-for="(activity, index) in items" :key="index" class="hv__item">
        <span class="hv__item-value">{{ formatValue(activity.value.toFixed(2)) }} {{ meta.unit }}</span>
        <span class="hv__item-date">{{ dateTimeFormat(activity.timestamp) }}</span>
        <span class="hv__item-user" v-if="activity.username">{{ activity.username }}</span>
        <span class="hv__item-comment" v-if="activity.comment">{{ activity.comment }}</span>
        <el-button
          v-if="activity.id && hasPermission('FIXER')"
          class="hv__del"
          icon="el-icon-delete"
          size="mini"
          circle
          title="Obriši stavku"
          @click="showDeleteConfirmDialog(activity)"
        ></el-button>
      </li>
    </ul>

    <el-dialog
      :title="'Nova stavka — ' + meta.title"
      :visible.sync="state.showAddDialog"
      width="440px"
      @opened="focusValue"
    >
      <div class="dlg__ctx">
        Trenutno: <strong>{{ formatValue(meta.value.toFixed(2)) }} {{ meta.unit }}</strong>
      </div>

      <div class="dlg__field">
        <label>Iznos ({{ meta.unit }}) <span class="req">*</span></label>
        <el-input-number
          ref="valueInput"
          class="dlg__number"
          v-model="state.addForm.value"
          :step="0.01"
          :precision="2"
          :min="0"
          controls-position="right"
          @keyup.enter.native="createValue"
        ></el-input-number>
        <div v-if="formError" class="dlg__err">{{ formError }}</div>
      </div>

      <div class="dlg__field">
        <label>Komentar</label>
        <el-input
          v-model="state.addForm.comment"
          placeholder="npr. Plaćene 4 rate"
          autocomplete="off"
          @keyup.enter.native="createValue"
        ></el-input>
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button @click="state.showAddDialog = false">Odustani</el-button>
        <el-button type="primary" :loading="saving" @click="createValue">Dodaj</el-button>
      </span>
    </el-dialog>

    <el-dialog title="Obrisati stavku?" :visible.sync="state.showDeleteConfirmDialog" width="400px">
      <div class="dlg__confirm">
        <div class="dlg__confirm-value">
          {{ formatValue(state.deleteForm.value.toFixed(2)) }} {{ meta.unit }}
        </div>
        <div class="dlg__confirm-sub">{{ meta.title }} · ova radnja se ne može poništiti</div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="state.showDeleteConfirmDialog = false">Odustani</el-button>
        <el-button type="danger" @click="deleteValue">Obriši</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import AuthService from "@/services/auth.service";
import API from "@/services/api/api";
import moment from "moment";

export default {
  name: "HistVal",
  props: {
    meta: Object,
    config: Object,
    items: Array,
    callback: Function,
    closed: Boolean,
  },
  data() {
    return {
      // The entries are the point of this tab, so they start open rather
      // than behind a toggle on every row.
      expanded: true,
      formError: "",
      saving: false,
      state: {
        addForm: {
          value: undefined,
          comment: "",
        },
        deleteForm: {
          value: 0.0,
        },
        showAddDialog: false,
        showDeleteConfirmDialog: false,
      },
    };
  },
  methods: {
    hasPermission: function (p) {
      return AuthService.hasPermission(p);
    },
    formatValue: function (nStr) {
      nStr += "";
      var x = nStr.split(".");
      var x1 = x[0];
      var x2 = x.length > 1 ? "." + x[1] : "";
      var rgx = /(\d+)(\d{3})/;
      while (rgx.test(x1)) {
        x1 = x1.replace(rgx, "$1" + "," + "$2");
      }
      return x1 + x2;
    },
    focusValue: function () {
      const el = this.$refs.valueInput;
      if (el && el.focus) el.focus();
    },
    showAddDialog: function () {
      this.state.addForm = { value: undefined, comment: "" };
      this.formError = "";
      this.state.showAddDialog = true;
    },
    showDeleteConfirmDialog: function (e) {
      this.state.deleteForm = e;
      this.state.showDeleteConfirmDialog = true;
    },
    dateTimeFormat: function (date) {
      if (typeof date == "undefined") {
        return "";
      }
      return moment(date).format("DD/MM/YYYY HH:mm");
    },
    deleteValue: async function () {
      if (this.state.deleteForm.id) {
        try {
          await API.Generic.delete("fin_value", this.state.deleteForm.id);
          this.state.showDeleteConfirmDialog = false;
          this.state.deleteForm = { value: 0.0 };
        } catch (e) {
          console.log(e);
        }
        if (this.callback) {
          this.callback();
        }
      }
    },
    createValue: async function () {
      const raw = this.state.addForm.value;
      // An empty or negative value used to return silently, so the dialog
      // looked like it was ignoring the click.
      if (raw === undefined || raw === null || raw === "") {
        this.formError = "Unesite iznos.";
        return;
      }
      const value = parseFloat(raw);
      if (isNaN(value)) {
        this.formError = "Iznos mora biti broj.";
        return;
      }
      if (value < 0) {
        this.formError = "Iznos ne može biti negativan.";
        return;
      }
      this.formError = "";
      this.saving = true;

      const fvForm = {
        finansije_id: this.config.fin_id.toString(),
        tip: this.config.type,
        status: 0,
        comment: this.state.addForm.comment || "",
        user_id: this.config.user_id.toString(),
        value: value,
      };

      try {
        await API.Generic.create("fin_value", fvForm);
        this.state.showAddDialog = false;
        this.expanded = true;
      } catch (e) {
        this.formError = "Stavka nije sačuvana — pokušajte ponovo.";
        console.log(e);
        return;
      } finally {
        this.saving = false;
      }
      if (this.callback) {
        this.callback();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.hv + .hv {
  border-top: 1px solid var(--line-soft);
}

.hv__row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 13px 0;
}

.hv__toggle {
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: var(--ink);
  cursor: pointer;
  min-width: 0;

  &:disabled {
    cursor: default;
    padding-left: 18px;
  }

  i {
    font-size: var(--fs-sm);
    color: var(--ink-4);
    width: 12px;
  }
}

.hv__label {
  font-size: var(--fs-md);
  white-space: nowrap;
}

.hv__count {
  font-size: var(--fs-xs);
  color: var(--ink-4);
  white-space: nowrap;
}

.hv__value {
  margin-left: auto;
  font-size: var(--fs-2xl);
  font-weight: 700;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.hv__value--zero {
  color: var(--ink-4);
  font-weight: 500;
}

.hv__unit {
  font-size: var(--fs-xs);
  font-weight: 500;
  color: var(--ink-3);
  margin-left: 4px;
}

.hv__add {
  flex-shrink: 0;
}

.hv__list {
  list-style: none;
  margin: 0 0 14px 0;
  padding: 6px 0 6px 20px;
  border-left: 2px solid var(--line);
}

.hv__item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 7px 0;
  font-size: var(--fs-sm);
}

.hv__item-value {
  font-weight: 600;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.hv__item-date {
  color: var(--ink-4);
  white-space: nowrap;
}

.hv__item-user {
  color: var(--ink-3);
  white-space: nowrap;
}

.hv__item-comment {
  color: var(--ink-2);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.hv__del {
  margin-left: auto;
  flex-shrink: 0;
}

.dlg__ctx {
  font-size: var(--fs-sm);
  color: var(--ink-3);
  margin-bottom: 16px;
}

.dlg__field {
  margin-bottom: 16px;

  label {
    display: block;
    font-size: var(--fs-sm);
    color: var(--ink-2);
    margin-bottom: 5px;
  }
}

.dlg__number {
  width: 100%;
}

.dlg__err {
  font-size: var(--fs-sm);
  color: var(--danger);
  margin-top: 5px;
}

.req {
  color: var(--danger);
}

.dlg__confirm {
  text-align: center;
  padding: 6px 0 2px 0;
}

.dlg__confirm-value {
  font-size: 22px;
  font-weight: 700;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
}

.dlg__confirm-sub {
  font-size: var(--fs-sm);
  color: var(--ink-3);
  margin-top: 4px;
}
</style>
